import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Footer from '../Components/Footer';
import Header from '../Components/Header';
import Auth from '../Function/Auth';
import { toast, ToastContainer } from "react-toastify";
import {
    PERMISSION_MODULES,
    getLoggedAdmin,
    isMasterAdmin
} from '../Function/Permission';

function Admin() {
    const authToken = sessionStorage.getItem('authToken');
    const rawBaseUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.marclife.io/api/admin';
    const baseurl = rawBaseUrl.trim();

    const loggedUser = getLoggedAdmin();
    const isCurrentMaster = isMasterAdmin(loggedUser);

    // Data States
    const [tableLoading, setTableLoading] = useState(false);
    const [adminList, setAdminList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRoleFilter, setSelectedRoleFilter] = useState('ALL');
    const [sortBy, setSortBy] = useState('newest');

    // Create Admin Form States
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [adminName, setAdminName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [adminRole, setAdminRole] = useState('Sub Admin');
    const [createPermissions, setCreatePermissions] = useState(() => {
        const initial = {};
        PERMISSION_MODULES.forEach(m => { initial[m.key] = 'none'; });
        return initial;
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Edit Admin Form States
    const [editAdmin, setEditAdmin] = useState(null);
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPassword, setEditPassword] = useState('');
    const [editRole, setEditRole] = useState('Sub Admin');
    const [editPermissions, setEditPermissions] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);

    // View Details & Delete Modals
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [adminToDelete, setAdminToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Helper: Normalize permissions object
    const normalizePermissions = (rawPerms) => {
        let perms = rawPerms;
        if (typeof perms === 'string') {
            try {
                perms = JSON.parse(perms);
            } catch (e) {
                perms = {};
            }
        }
        if (!perms || typeof perms !== 'object') perms = {};

        const res = {};
        PERMISSION_MODULES.forEach(m => {
            const val = (perms[m.key] || 'none').toLowerCase().trim();
            if (val === 'editor' || val === 'edit') res[m.key] = 'editor';
            else if (val === 'viewer' || val === 'view') res[m.key] = 'viewer';
            else res[m.key] = 'none';
        });
        return res;
    };

    // Fetch Admins List
    const fetchAdmins = useCallback(async () => {
        setTableLoading(true);
        try {
            const response = await fetch(`${baseurl}/list`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                }
            });

            const result = await response.json();
            if (result.status) {
                setAdminList(Array.isArray(result.data) ? result.data : []);
            } else {
                toast.error(result.message || 'Failed to fetch admin list');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            toast.error("Network or server error while fetching admins");
        } finally {
            setTableLoading(false);
        }
    }, [authToken, baseurl]);

    useEffect(() => {
        fetchAdmins();
    }, [fetchAdmins]);

    // Copy to clipboard helper
    const copyToClipboard = (text, label = 'Copied') => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            toast.success(`${label} copied to clipboard!`);
        }).catch(() => {
            toast.error('Failed to copy');
        });
    };

    // Delete Admin handler
    const handleDeleteAdmin = async () => {
        if (!adminToDelete) return;
        setIsDeleting(true);

        try {
            const response = await fetch(`${baseurl}/delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    admin_id: adminToDelete.id,
                }),
            });

            const result = await response.json();
            if (result.status) {
                toast.success(result.message || "Admin removed successfully");
                setAdminList(prev => prev.filter(item => item.id !== adminToDelete.id));
                setAdminToDelete(null);
            } else {
                toast.error(result.message || "Failed to delete admin.");
            }
        } catch (error) {
            console.error("Error deleting admin:", error);
            toast.error("Server error while deleting admin.");
        } finally {
            setIsDeleting(false);
        }
    };

    // Create Admin handler
    const handleCreateAdmin = async (e) => {
        if (e) e.preventDefault();

        if (!adminName.trim() || !adminEmail.trim() || !adminPassword || !confirmPassword) {
            toast.warn("Please complete all required fields.");
            return;
        }

        if (adminPassword.length < 6) {
            toast.warn("Password must be at least 6 characters long.");
            return;
        }

        if (adminPassword !== confirmPassword) {
            toast.error("Passwords do not match.");
            return;
        }

        setIsSubmitting(true);

        try {
            const permissionsToSave = adminRole === 'Master Admin' ? null : createPermissions;

            const response = await fetch(`${baseurl}/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    name: adminName.trim(),
                    email: adminEmail.trim(),
                    password: adminPassword,
                    user_role: adminRole,
                    permissions: permissionsToSave,
                }),
            });

            const result = await response.json();
            if (result.status) {
                toast.success(result.message || "Admin created successfully!");
                fetchAdmins();
                setShowCreateModal(false);
                setAdminName('');
                setAdminEmail('');
                setAdminPassword('');
                setConfirmPassword('');
                setAdminRole('Sub Admin');
                // Reset permissions
                const reset = {};
                PERMISSION_MODULES.forEach(m => { reset[m.key] = 'none'; });
                setCreatePermissions(reset);
            } else {
                toast.error(result.message || "Failed to create admin.");
            }
        } catch (error) {
            console.error("Error creating admin:", error);
            toast.error("Server error while creating admin.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Open Edit Modal with selected admin
    const openEditModal = (admin) => {
        setEditAdmin(admin);
        setEditName(admin.name || '');
        setEditEmail(admin.email || '');
        setEditPassword('');
        setEditRole(admin.user_role || 'Sub Admin');
        setEditPermissions(normalizePermissions(admin.permissions));
    };

    // Update Admin handler
    const handleUpdateAdmin = async (e) => {
        if (e) e.preventDefault();
        if (!editAdmin) return;

        if (!editName.trim() || !editEmail.trim()) {
            toast.warn("Name and Email are required.");
            return;
        }

        if (editPassword && editPassword.length < 6) {
            toast.warn("Password must be at least 6 characters long.");
            return;
        }

        setIsUpdating(true);

        try {
            const permissionsToSave = editRole === 'Master Admin' ? null : editPermissions;

            const payload = {
                admin_id: editAdmin.id,
                name: editName.trim(),
                email: editEmail.trim(),
                user_role: editRole,
                permissions: permissionsToSave,
            };

            if (editPassword) {
                payload.password = editPassword;
            }

            const response = await fetch(`${baseurl}/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();
            if (result.status) {
                toast.success(result.message || "Admin permissions updated successfully!");
                fetchAdmins();
                setEditAdmin(null);
            } else {
                toast.error(result.message || "Failed to update admin.");
            }
        } catch (error) {
            console.error("Error updating admin:", error);
            toast.error("Server error while updating admin.");
        } finally {
            setIsUpdating(false);
        }
    };

    // Filter & Sort Admins
    const filteredAdmins = useMemo(() => {
        return adminList.filter(item => {
            const nameMatch = item.name ? item.name.toLowerCase().includes(searchQuery.toLowerCase()) : false;
            const emailMatch = item.email ? item.email.toLowerCase().includes(searchQuery.toLowerCase()) : false;
            const matchesSearch = !searchQuery || nameMatch || emailMatch;

            const matchesRole = selectedRoleFilter === 'ALL' ||
                (item.user_role && item.user_role.toLowerCase() === selectedRoleFilter.toLowerCase());

            return matchesSearch && matchesRole;
        }).sort((a, b) => {
            if (sortBy === 'name') {
                return (a.name || '').localeCompare(b.name || '');
            } else if (sortBy === 'oldest') {
                return new Date(a.created_at || 0) - new Date(b.created_at || 0);
            }
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
    }, [adminList, searchQuery, selectedRoleFilter, sortBy]);

    // Metrics counts
    const metrics = useMemo(() => {
        const total = adminList.length;
        const masterCount = adminList.filter(a => (a.user_role || '').toLowerCase().includes('master')).length;
        const subCount = adminList.filter(a => (a.user_role || '').toLowerCase().includes('sub')).length;
        return { total, masterCount, subCount };
    }, [adminList]);

    // Role badge helper
    const getRoleBadge = (role) => {
        const r = (role || '').toLowerCase();
        if (r.includes('master')) {
            return (
                <span className="badge bg-primary-gradient text-fixed-white px-2 py-1 fs-11 fw-semibold rounded-pill">
                    <i className="fa fa-crown me-1"></i> Master Admin
                </span>
            );
        }
        return (
            <span className="badge bg-secondary-transparent text-secondary px-2 py-1 fs-11 fw-semibold rounded-pill">
                <i className="fa fa-user-shield me-1"></i> Admin
            </span>
        );
    };

    // Avatar background generator
    const getAvatarBg = (name) => {
        const colors = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6'];
        let hash = 0;
        for (let i = 0; i < (name || '').length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    const getInitials = (name) => {
        if (!name) return 'SA';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        return name.slice(0, 2).toUpperCase();
    };

    // Permission Matrix helper buttons for Bulk apply
    const applyBulkPermissions = (targetState, setTargetState, level) => {
        const updated = { ...targetState };
        PERMISSION_MODULES.forEach(m => {
            updated[m.key] = level;
        });
        setTargetState(updated);
    };

    return (
        <>
            <Header>
                <Auth />
                <div className="main-content app-content">
                    <div className="container-fluid">

                        {/* Page Header */}
                        <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
                            <div>
                                <h4 className="mb-0 fw-bold text-dark">Admin & Permissions Management</h4>
                                <p className="mb-0 text-muted fs-13">
                                    Configure granular access permissions for admins across all platform sections (Viewer vs Editor).
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
                                {isCurrentMaster && (
                                    <button
                                        type="button"
                                        className="btn btn-primary d-inline-flex align-items-center gap-2 shadow-sm"
                                        onClick={() => setShowCreateModal(true)}
                                    >
                                        <i className="fa fa-user-plus"></i>
                                        <span>+ Admin</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                                    onClick={fetchAdmins}
                                    disabled={tableLoading}
                                >
                                    <i className={`fa fa-refresh ${tableLoading ? 'fa-spin' : ''}`}></i>
                                    <span>Refresh</span>
                                </button>
                            </div>
                        </div>

                        {/* Summary Metrics Cards */}
                        <div className="row g-3 mb-4">
                            <div className="col-xl-4 col-md-6 col-12">
                                <div className="card border-0 shadow-sm rounded-3 overflow-hidden bg-primary-gradient text-fixed-white h-100">
                                    <div className="p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span className="fs-12 text-uppercase fw-semibold op-8">Total Administrators</span>
                                            <span className="badge bg-white-2 rounded-pill px-2 py-1 fs-11">Team Total</span>
                                        </div>
                                        <h3 className="fs-24 fw-bold mb-0 mt-2">{metrics.total}</h3>
                                        <span className="fs-11 op-7">All registered personnel accounts</span>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-4 col-md-6 col-12">
                                <div className="card border-0 shadow-sm rounded-3 overflow-hidden bg-success-gradient text-fixed-white h-100">
                                    <div className="p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span className="fs-12 text-uppercase fw-semibold op-8">Master Admins</span>
                                            <span className="badge bg-white-2 rounded-pill px-2 py-1 fs-11">Full Superuser</span>
                                        </div>
                                        <h3 className="fs-24 fw-bold mb-0 mt-2">{metrics.masterCount}</h3>
                                        <span className="fs-11 op-7">Unrestricted Editor permissions</span>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-4 col-md-6 col-12">
                                <div className="card border-0 shadow-sm rounded-3 overflow-hidden bg-warning-gradient text-fixed-white h-100">
                                    <div className="p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span className="fs-12 text-uppercase fw-semibold op-8">Admins</span>
                                            <span className="badge bg-white-2 rounded-pill px-2 py-1 fs-11">Custom Access</span>
                                        </div>
                                        <h3 className="fs-24 fw-bold mb-0 mt-2">{metrics.subCount}</h3>
                                        <span className="fs-11 op-7">Configured with file-wise Viewer / Editor rules</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search & Filter Bar */}
                        <div className="card border-0 shadow-sm rounded-3 mb-4">
                            <div className="card-body p-3">
                                <div className="row g-2 align-items-center">
                                    <div className="col-md-5 col-12">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-light border-end-0">
                                                <i className="fa fa-search text-muted"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control border-start-0"
                                                placeholder="Search by admin name or email..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            {searchQuery && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary border-start-0"
                                                    onClick={() => setSearchQuery('')}
                                                    title="Clear search"
                                                >
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-3 col-6">
                                        <select
                                            className="form-select form-select-sm"
                                            value={selectedRoleFilter}
                                            onChange={(e) => setSelectedRoleFilter(e.target.value)}
                                        >
                                            <option value="ALL">All Roles</option>
                                            <option value="Master Admin">Master Admin</option>
                                            <option value="Sub Admin">Sub Admin</option>
                                        </select>
                                    </div>

                                    <div className="col-md-3 col-6">
                                        <select
                                            className="form-select form-select-sm"
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                        >
                                            <option value="newest">Sort: Newest First</option>
                                            <option value="oldest">Sort: Oldest First</option>
                                            <option value="name">Sort: Name (A-Z)</option>
                                        </select>
                                    </div>

                                    <div className="col-md-1 col-12 text-md-end text-start">
                                        <span className="badge bg-light text-muted border px-2 py-1">
                                            {filteredAdmins.length} {filteredAdmins.length === 1 ? 'user' : 'users'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Admins Table Card */}
                        <div className="card border-0 shadow-sm rounded-3 mb-4">
                            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                                <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                    <i className="fa fa-user-shield text-primary"></i>
                                    <span>Team & Admin Accounts</span>
                                </h6>
                                <span className="fs-12 text-muted">Permission-Controlled Personnel</span>
                            </div>

                            <div className="table-responsive country-table">
                                {tableLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mt-2 fs-13">Loading administrators...</p>
                                    </div>
                                ) : filteredAdmins.length > 0 ? (
                                    <table className="table table-hover align-middle mb-0 text-nowrap">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col" className="ps-3" style={{ width: '60px' }}>Avatar</th>
                                                <th scope="col">Name & ID</th>
                                                <th scope="col">Email</th>
                                                <th scope="col">Role</th>
                                                <th scope="col">Module Permissions Summary</th>
                                                <th scope="col">Last Login</th>
                                                <th scope="col" className="text-end pe-3" style={{ width: '150px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAdmins.map((admin, idx) => {
                                                const isMaster = (admin.user_role || '').toLowerCase().includes('master');
                                                const perms = normalizePermissions(admin.permissions);
                                                const accessibleModules = PERMISSION_MODULES.filter(m => perms[m.key] !== 'none');
                                                const editorCount = PERMISSION_MODULES.filter(m => perms[m.key] === 'editor').length;
                                                const viewerCount = PERMISSION_MODULES.filter(m => perms[m.key] === 'viewer').length;

                                                return (
                                                    <tr key={admin.id || idx}>
                                                        <td className="ps-3">
                                                            <div
                                                                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                                                                style={{
                                                                    width: '38px',
                                                                    height: '38px',
                                                                    backgroundColor: getAvatarBg(admin.name || 'Admin'),
                                                                    fontSize: '13px'
                                                                }}
                                                            >
                                                                {getInitials(admin.name || 'SA')}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold text-dark">{admin.name || 'Unnamed Admin'}</div>
                                                            <div className="text-muted small fs-12">ID: #{admin.id || 'N/A'}</div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-1 font-monospace fs-12">
                                                                <span className="text-dark">{admin.email || 'N/A'}</span>
                                                                {admin.email && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-link p-0 text-muted ms-1"
                                                                        onClick={() => copyToClipboard(admin.email, 'Email')}
                                                                        title="Copy email address"
                                                                    >
                                                                        <i className="fa fa-copy"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {getRoleBadge(admin.user_role)}
                                                        </td>
                                                        <td>
                                                            {isMaster ? (
                                                                <span className="badge bg-success-transparent text-success px-2 py-1 fs-11">
                                                                    <i className="fa fa-check-circle me-1"></i> Full Access (All 11 Modules)
                                                                </span>
                                                            ) : accessibleModules.length === 0 ? (
                                                                <span className="badge bg-danger-transparent text-danger px-2 py-1 fs-11">
                                                                    <i className="fa fa-ban me-1"></i> No Modules Permitted
                                                                </span>
                                                            ) : (
                                                                <div className="d-flex flex-wrap align-items-center gap-1" style={{ maxWidth: '340px' }}>
                                                                    <span className="badge bg-primary-transparent text-primary fs-11 px-2 py-1">
                                                                        {accessibleModules.length} Modules Allowed
                                                                    </span>
                                                                    {editorCount > 0 && (
                                                                        <span className="badge bg-success-transparent text-success fs-10 px-2 py-1">
                                                                            ✏️ {editorCount} Editor
                                                                        </span>
                                                                    )}
                                                                    {viewerCount > 0 && (
                                                                        <span className="badge bg-info-transparent text-info fs-10 px-2 py-1">
                                                                            👁️ {viewerCount} Viewer
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="text-muted fs-12">
                                                                {admin.last_login || admin.created_at || 'Never'}
                                                            </span>
                                                        </td>
                                                        <td className="text-end pe-3">
                                                            <div className="btn-group btn-group-sm">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-info"
                                                                    onClick={() => setSelectedAdmin(admin)}
                                                                    title="View Details"
                                                                >
                                                                    <i className="fa fa-eye"></i>
                                                                </button>
                                                                {isCurrentMaster && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-primary"
                                                                            onClick={() => openEditModal(admin)}
                                                                            title="Edit Admin & Permissions"
                                                                        >
                                                                            <i className="fa fa-edit"></i>
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-danger"
                                                                            onClick={() => setAdminToDelete(admin)}
                                                                            title="Delete Admin"
                                                                            disabled={admin.id === loggedUser?.id}
                                                                        >
                                                                            <i className="fa fa-trash"></i>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="text-center py-5">
                                        <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center text-muted mb-3" style={{ width: '64px', height: '64px' }}>
                                            <i className="fa fa-user-slash fs-3"></i>
                                        </div>
                                        <h6 className="fw-semibold text-dark">No administrators found</h6>
                                        <p className="text-muted fs-13 mb-3">
                                            {searchQuery || selectedRoleFilter !== 'ALL'
                                                ? 'No administrators match your current filter criteria.'
                                                : 'No administrator accounts exist currently.'}
                                        </p>
                                        {isCurrentMaster && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={() => setShowCreateModal(true)}
                                            >
                                                + Add First Admin
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* MODAL: Create Sub-Admin with Granular Permissions */}
                {showCreateModal && (
                    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content border-0 shadow-lg rounded-3">
                                <div className="modal-header bg-primary text-white py-3">
                                    <h5 className="modal-title fs-16 fw-bold">
                                        <i className="fa fa-user-plus me-2"></i>Create New Admin
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setShowCreateModal(false)}
                                        disabled={isSubmitting}
                                    ></button>
                                </div>
                                <form onSubmit={handleCreateAdmin}>
                                    <div className="modal-body p-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                                        {/* Account Credentials */}
                                        <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                                            <i className="fa fa-id-card text-primary me-2"></i>Account Details
                                        </h6>
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-6 col-12">
                                                <label className="form-label fs-12 fw-semibold text-muted mb-1">Full Name *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g. John Doe"
                                                    value={adminName}
                                                    onChange={(e) => setAdminName(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6 col-12">
                                                <label className="form-label fs-12 fw-semibold text-muted mb-1">Email Address *</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    placeholder="subadmin@marclife.io"
                                                    value={adminEmail}
                                                    onChange={(e) => setAdminEmail(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-4 col-12">
                                                <label className="form-label fs-12 fw-semibold text-muted mb-1">Role *</label>
                                                <select
                                                    className="form-select"
                                                    value={adminRole}
                                                    onChange={(e) => setAdminRole(e.target.value)}
                                                >
                                                    <option value="Sub Admin">Admin (Granular Permissions)</option>
                                                    <option value="Master Admin">Master Admin (Full Superuser)</option>
                                                </select>
                                            </div>
                                            <div className="col-md-4 col-12">
                                                <label className="form-label fs-12 fw-semibold text-muted mb-1">Password *</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    placeholder="Min. 6 characters"
                                                    value={adminPassword}
                                                    onChange={(e) => setAdminPassword(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-4 col-12">
                                                <label className="form-label fs-12 fw-semibold text-muted mb-1">Confirm Password *</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    placeholder="Repeat password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Module-Wise Permissions Matrix */}
                                        {adminRole === 'Master Admin' ? (
                                            <div className="alert alert-info border-0 rounded-3 d-flex align-items-center gap-2">
                                                <i className="fa fa-info-circle fs-18"></i>
                                                <span className="fs-13">
                                                    <strong>Master Admin Role:</strong> This account will inherently possess complete <strong>Editor</strong> access to all modules and system controls.
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                                                    <h6 className="fw-bold text-dark mb-0">
                                                        <i className="fa fa-lock text-primary me-2"></i>Module Access & Action Permissions
                                                    </h6>
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-success"
                                                            onClick={() => applyBulkPermissions(createPermissions, setCreatePermissions, 'editor')}
                                                            title="Set all modules to Editor"
                                                        >
                                                            ✏️ All Editor
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-info"
                                                            onClick={() => applyBulkPermissions(createPermissions, setCreatePermissions, 'viewer')}
                                                            title="Set all modules to Viewer"
                                                        >
                                                            👁️ All Viewer
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            onClick={() => applyBulkPermissions(createPermissions, setCreatePermissions, 'none')}
                                                            title="Clear all permissions"
                                                        >
                                                            ❌ Clear All
                                                        </button>
                                                    </div>
                                                </div>

                                                <p className="text-muted fs-12 mb-3">
                                                    Choose whether this admin can <strong>View Only</strong> or <strong>Edit & Perform Actions</strong> on each section. Modules marked <strong>No Access</strong> will be completely hidden from their sidebar and direct URL access blocked.
                                                </p>

                                                <div className="table-responsive border rounded-3">
                                                    <table className="table table-sm align-middle mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={{ width: '40%' }}>Section / Module</th>
                                                                <th className="text-center" style={{ width: '20%' }}>No Access</th>
                                                                <th className="text-center" style={{ width: '20%' }}>Viewer (View Only)</th>
                                                                <th className="text-center" style={{ width: '20%' }}>Editor (View & Edit)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {PERMISSION_MODULES.map((mod) => {
                                                                const currentVal = createPermissions[mod.key] || 'none';
                                                                return (
                                                                    <tr key={mod.key}>
                                                                        <td>
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <i className={`${mod.icon} text-muted`}></i>
                                                                                <div>
                                                                                    <div className="fw-semibold text-dark fs-13">{mod.label}</div>
                                                                                    <small className="text-muted fs-11">{mod.description}</small>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="radio"
                                                                                className="form-check-input"
                                                                                name={`perm_create_${mod.key}`}
                                                                                checked={currentVal === 'none'}
                                                                                onChange={() => setCreatePermissions(prev => ({ ...prev, [mod.key]: 'none' }))}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="radio"
                                                                                className="form-check-input"
                                                                                name={`perm_create_${mod.key}`}
                                                                                checked={currentVal === 'viewer'}
                                                                                onChange={() => setCreatePermissions(prev => ({ ...prev, [mod.key]: 'viewer' }))}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="radio"
                                                                                className="form-check-input"
                                                                                name={`perm_create_${mod.key}`}
                                                                                checked={currentVal === 'editor'}
                                                                                onChange={() => setCreatePermissions(prev => ({ ...prev, [mod.key]: 'editor' }))}
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="modal-footer bg-light py-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => setShowCreateModal(false)}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting && <i className="fa fa-spinner fa-spin"></i>}
                                            <span>Create Admin Account</span>
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL: Edit Sub-Admin & Permissions Matrix */}
                {editAdmin && (
                    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content border-0 shadow-lg rounded-3">
                                <div className="modal-header bg-primary text-white py-3">
                                    <h5 className="modal-title fs-16 fw-bold">
                                        <i className="fa fa-user-shield me-2"></i>Manage Admin Permissions: {editAdmin.name}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setEditAdmin(null)}
                                        disabled={isUpdating}
                                    ></button>
                                </div>
                                <form onSubmit={handleUpdateAdmin}>
                                    <div className="modal-body p-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                                        {/* Account Info */}
                                        <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                                            <i className="fa fa-user-edit text-primary me-2"></i>Account Profile
                                        </h6>
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-6 col-12">
                                                <label className="form-label fs-12 fw-semibold text-muted mb-1">Full Name *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6 col-12">
                                                <label className="form-label fs-12 fw-semibold text-muted mb-1">Email Address *</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    value={editEmail}
                                                    onChange={(e) => setEditEmail(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6 col-12">
                                                <label className="form-label fs-12 fw-semibold text-muted mb-1">Role *</label>
                                                <select
                                                    className="form-select"
                                                    value={editRole}
                                                    onChange={(e) => setEditRole(e.target.value)}
                                                >
                                                    <option value="Sub Admin">Admin (Granular Permissions)</option>
                                                    <option value="Master Admin">Master Admin (Full Superuser)</option>
                                                </select>
                                            </div>
                                            <div className="col-md-6 col-12">
                                                <label className="form-label fs-12 fw-semibold text-muted mb-1">New Password (Leave blank to keep current)</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    placeholder="Optional new password"
                                                    value={editPassword}
                                                    onChange={(e) => setEditPassword(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* Permissions Matrix */}
                                        {editRole === 'Master Admin' ? (
                                            <div className="alert alert-info border-0 rounded-3 d-flex align-items-center gap-2">
                                                <i className="fa fa-info-circle fs-18"></i>
                                                <span className="fs-13">
                                                    <strong>Master Admin Role:</strong> Unrestricted access granted to all modules automatically.
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                                                    <h6 className="fw-bold text-dark mb-0">
                                                        <i className="fa fa-lock text-primary me-2"></i>Module Access Matrix
                                                    </h6>
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-success"
                                                            onClick={() => applyBulkPermissions(editPermissions, setEditPermissions, 'editor')}
                                                            title="Set all modules to Editor"
                                                        >
                                                            ✏️ All Editor
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-info"
                                                            onClick={() => applyBulkPermissions(editPermissions, setEditPermissions, 'viewer')}
                                                            title="Set all modules to Viewer"
                                                        >
                                                            👁️ All Viewer
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            onClick={() => applyBulkPermissions(editPermissions, setEditPermissions, 'none')}
                                                            title="Clear all permissions"
                                                        >
                                                            ❌ Clear All
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="table-responsive border rounded-3">
                                                    <table className="table table-sm align-middle mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={{ width: '40%' }}>Section / Module</th>
                                                                <th className="text-center" style={{ width: '20%' }}>No Access</th>
                                                                <th className="text-center" style={{ width: '20%' }}>Viewer (View Only)</th>
                                                                <th className="text-center" style={{ width: '20%' }}>Editor (View & Edit)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {PERMISSION_MODULES.map((mod) => {
                                                                const currentVal = editPermissions[mod.key] || 'none';
                                                                return (
                                                                    <tr key={mod.key}>
                                                                        <td>
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <i className={`${mod.icon} text-muted`}></i>
                                                                                <div>
                                                                                    <div className="fw-semibold text-dark fs-13">{mod.label}</div>
                                                                                    <small className="text-muted fs-11">{mod.description}</small>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="radio"
                                                                                className="form-check-input"
                                                                                name={`perm_edit_${mod.key}`}
                                                                                checked={currentVal === 'none'}
                                                                                onChange={() => setEditPermissions(prev => ({ ...prev, [mod.key]: 'none' }))}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="radio"
                                                                                className="form-check-input"
                                                                                name={`perm_edit_${mod.key}`}
                                                                                checked={currentVal === 'viewer'}
                                                                                onChange={() => setEditPermissions(prev => ({ ...prev, [mod.key]: 'viewer' }))}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="radio"
                                                                                className="form-check-input"
                                                                                name={`perm_edit_${mod.key}`}
                                                                                checked={currentVal === 'editor'}
                                                                                onChange={() => setEditPermissions(prev => ({ ...prev, [mod.key]: 'editor' }))}
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="modal-footer bg-light py-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => setEditAdmin(null)}
                                            disabled={isUpdating}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                                            disabled={isUpdating}
                                        >
                                            {isUpdating && <i className="fa fa-spinner fa-spin"></i>}
                                            <span>Save Changes & Permissions</span>
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL: View Details */}
                {selectedAdmin && (
                    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-3">
                                <div className="modal-header bg-primary text-white py-3">
                                    <h5 className="modal-title fs-16 fw-bold">
                                        <i className="fa fa-user me-2"></i>Admin Details #{selectedAdmin.id}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setSelectedAdmin(null)}
                                    ></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 mb-3">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                backgroundColor: getAvatarBg(selectedAdmin.name || 'Admin'),
                                                fontSize: '18px'
                                            }}
                                        >
                                            {getInitials(selectedAdmin.name || 'SA')}
                                        </div>
                                        <div>
                                            <h5 className="mb-0 fw-bold text-dark">{selectedAdmin.name}</h5>
                                            <div className="text-muted fs-12">{selectedAdmin.email}</div>
                                            <div className="mt-1">{getRoleBadge(selectedAdmin.user_role)}</div>
                                        </div>
                                    </div>

                                    <h6 className="fw-bold text-dark border-bottom pb-2 mb-2">Granted Module Permissions:</h6>
                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                        {selectedAdmin.user_role === 'Master Admin' ? (
                                            <span className="badge bg-success-transparent text-success p-2 w-100 text-start">
                                                🌟 Master Admin has full Editor control across all 11 modules.
                                            </span>
                                        ) : (
                                            (() => {
                                                const perms = normalizePermissions(selectedAdmin.permissions);
                                                const allowed = PERMISSION_MODULES.filter(m => perms[m.key] !== 'none');
                                                if (allowed.length === 0) {
                                                    return <span className="text-muted fs-12">No modules currently granted.</span>;
                                                }
                                                return allowed.map(m => (
                                                    <span
                                                        key={m.key}
                                                        className={`badge ${perms[m.key] === 'editor' ? 'bg-success-transparent text-success' : 'bg-info-transparent text-info'} p-2`}
                                                    >
                                                        {perms[m.key] === 'editor' ? '✏️' : '👁️'} {m.label}: {perms[m.key].toUpperCase()}
                                                    </span>
                                                ));
                                            })()
                                        )}
                                    </div>

                                    <div className="row g-2 fs-12 border-top pt-2 text-muted">
                                        <div className="col-6">Login Count: <span className="fw-bold text-dark">{selectedAdmin.login_count || 0}</span></div>
                                        <div className="col-6">Status: <span className="fw-bold text-success">{selectedAdmin.login_status || 'Active'}</span></div>
                                        <div className="col-12">Last Login: <span className="fw-bold text-dark">{selectedAdmin.last_login || 'Never'}</span></div>
                                        <div className="col-12">Created Date: <span className="fw-bold text-dark">{selectedAdmin.created_at || 'N/A'}</span></div>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light py-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setSelectedAdmin(null)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL: Delete Confirm */}
                {adminToDelete && (
                    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-3">
                                <div className="modal-header bg-danger text-white py-3">
                                    <h5 className="modal-title fs-16 fw-bold">
                                        <i className="fa fa-exclamation-triangle me-2"></i>Delete Administrator Account
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setAdminToDelete(null)}
                                        disabled={isDeleting}
                                    ></button>
                                </div>
                                <div className="modal-body p-4 text-center">
                                    <div className="mb-3">
                                        <i className="fa fa-trash-alt text-danger fs-50"></i>
                                    </div>
                                    <h5 className="fw-bold mb-2">Delete {adminToDelete.name}?</h5>
                                    <p className="text-muted fs-13 mb-0">
                                        Are you sure you want to delete administrator <strong>{adminToDelete.email}</strong>? This action cannot be reversed and will revoke all access immediately.
                                    </p>
                                </div>
                                <div className="modal-footer bg-light py-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setAdminToDelete(null)}
                                        disabled={isDeleting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm d-inline-flex align-items-center gap-2"
                                        onClick={handleDeleteAdmin}
                                        disabled={isDeleting}
                                    >
                                        {isDeleting && <i className="fa fa-spinner fa-spin"></i>}
                                        <span>Confirm Delete</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <ToastContainer />
            </Header>
            <Footer />
        </>
    );
}

export default Admin;
